import { useState, useCallback, useEffect } from 'react';
import { X } from 'lucide-react';
import { LoadingSpinner } from '../LoadingSpinner';
import { useNotifications } from '../../contexts/NotificationContext';
import { useClickOutside } from '../../hooks/useClickOutside';
import useEscapeKey from '../../hooks/useEscapeKey';
import { motion } from "framer-motion";
import ValidatedInput from '../shared/ValidatedInput';
import { DEFAULT_VALIDATION_CONFIG, validateAndSanitizeInput, isInputValid } from '../../utils/validation/inputValidation';

interface RenameSchemaDialogProps {
  open: boolean;
  onClose: () => void;
  currentSchemaName: string;
  currentSchemaDescription?: string;
  onRenameConfirm: (newName: string, newDescription: string) => Promise<void>;
}

const RenameSchemaDialog: React.FC<RenameSchemaDialogProps> = ({
  open,
  onClose,
  currentSchemaName,
  currentSchemaDescription,
  onRenameConfirm,
}) => {
  const { showNotification } = useNotifications();
  const [name, setName] = useState(currentSchemaName);
  const [description, setDescription] = useState(currentSchemaDescription || '');
  
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleDirectClose = () => {
    onClose();
  };
  
  const dialogRef = useClickOutside<HTMLDivElement>(handleDirectClose);
  useEscapeKey(handleDirectClose);

  useEffect(() => {
    if (open) {
      setName(currentSchemaName);
      setDescription(currentSchemaDescription || '');
      setNameError('');
      setDescriptionError('');
      setIsLoading(false);
    }
  }, [open, currentSchemaName, currentSchemaDescription]);

  const validateName = useCallback(() => {
    const trimmedName = name.trim();
    if (!isInputValid(trimmedName, DEFAULT_VALIDATION_CONFIG.projectName)) { // Re-using projectName validation config
      if (trimmedName.length === 0 && !DEFAULT_VALIDATION_CONFIG.projectName.allowEmpty) {
        setNameError('Schema name is required.');
        return false;
      }
      if (trimmedName.length < (DEFAULT_VALIDATION_CONFIG.projectName.minLength || 0)) {
        setNameError(`Schema name must be at least ${DEFAULT_VALIDATION_CONFIG.projectName.minLength} characters.`);
        return false;
      }
    }
    setNameError('');
    return true;
  }, [name]);

  const validateDescription = useCallback(() => {
    // Description is optional, so only validate if there's content beyond max length
    if (description.length > DEFAULT_VALIDATION_CONFIG.projectDescription.maxLength) { // Re-using projectDescription validation config
      setDescriptionError(`Description cannot exceed ${DEFAULT_VALIDATION_CONFIG.projectDescription.maxLength} characters.`);
      return false;
    }
    setDescriptionError('');
    return true;
  }, [description]);

  const handleRename = useCallback(async () => {
    const isNameValid = validateName();
    const isDescriptionValid = validateDescription();

    if (!isNameValid || !isDescriptionValid) {
      return;
    }

    try {
      setIsLoading(true);
      const sanitizedName = validateAndSanitizeInput(name, DEFAULT_VALIDATION_CONFIG.projectName);
      const sanitizedDescription = validateAndSanitizeInput(description, DEFAULT_VALIDATION_CONFIG.projectDescription);
      
      await onRenameConfirm(sanitizedName, sanitizedDescription); 

      onClose();
    } catch (error) {
      console.error('Error updating schema:', error);
      showNotification('error', 'Failed to update schema.'); // Added period for consistency
    } finally {
      setIsLoading(false);
    }
  }, [name, description, onRenameConfirm, showNotification, onClose, validateName, validateDescription]);

  if (!open) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[999]" // Increased z-index
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        ref={dialogRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[480px] mx-4 border border-gray-200 dark:border-gray-700"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        <div className="px-6 pt-5 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Rename Schema</h2>
            <button
              onClick={handleDirectClose}
              className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <ValidatedInput
              id="schemaName"
              label="Schema Name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) validateName(); // Re-validate on change if there was an error
              }}
              onBlur={validateName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (validateName() && validateDescription()) {
                    handleRename();
                  }
                }
              }}
              validationConfig={DEFAULT_VALIDATION_CONFIG.projectName} // Re-using project name validation
              placeholder="Enter schema name"
              autoFocus={true}
              errorMessage={nameError}
            />
            <div>
              <ValidatedInput
                id="schemaDescription"
                label="Schema Description (Optional)"
                isTextarea={true}
                value={description}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setDescription(e.target.value);
                  if (descriptionError) validateDescription(); // Re-validate on change if there was an error
                }}
                onBlur={validateDescription}
                validationConfig={DEFAULT_VALIDATION_CONFIG.projectDescription} // Re-using project description validation
                placeholder="Enter a brief description for your schema"
                rows={3}
                errorMessage={descriptionError}
              />
              {DEFAULT_VALIDATION_CONFIG.projectDescription.maxLength && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
                  {description.length}/{DEFAULT_VALIDATION_CONFIG.projectDescription.maxLength}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 rounded-b-lg border-t border-gray-200 dark:border-gray-800">
          <div className="flex justify-end gap-3">
            <button
              onClick={handleDirectClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleRename}
              disabled={isLoading || !!nameError || !!descriptionError || !name.trim()}
              className={`
            px-4 py-2 text-sm font-medium rounded-md inline-flex items-center gap-2
            ${
              isLoading || !!nameError || !!descriptionError || !name.trim()
                ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }
          `}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="text-gray-500 dark:text-gray-400" />
                  <span>Saving...</span>
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default RenameSchemaDialog;
